import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type {
  GableRoofVerticalRails,
  GableRoofExtrusion,
  GableRoofConvexVolume,
} from "building-types/roofs/gableRoofExtrusionTypes";
import type { RoofSlope } from "building-types/roofs/roofSlope";
import type { GeneratedRoofVolumeLegacy } from "building-types/roofVolumes/roofVolumeTypes";
import type { EvaluatedRoofSlopeOptions } from "building-types/settings/roofBaseTypes";
import { RoofTruncationType, TruncationSlopeType, type RoofSlopeOptions } from "building-types/settings/roofBaseTypes";
import type { RoofSettings } from "building-types/settings/roofSettings";
import type {
  NeighborRoof,
  NeighborRoofs,
  RoofHeelHeightOffsets,
  RoofTruncationOptions,
} from "building-types/settings/roofTypes";
import { RoofSide } from "building-types/settings/roofTypes";
import { GableRoofType } from "building-types/settings/settingsTypesEnum";
import type { WallSettings } from "building-types/settings/wallSettings";
import { GABLE_ROOF_THICKNESS } from "common/constants/buildingObjects";
import type { Table } from "common/data/table";
import { assertIsNotNull } from "common/utils/assertOfType";
import { isBoxEave } from "doc/fasciaTopTrimSizeToValue";
import { DEFAULT_EAVE_RETURN_DISTANCE } from "doc/settings/defaultSettings";
import {
  MULTIGABLE_BSP_EPSILON,
  roofSlopeOptionsNew,
  neighborRoofNew,
  evaluatedRoofSlopeOptionsNew,
} from "doc/utils/roofTools";
import { getExteriorWallOffset } from "doc/utils/wallTypeTools";
import type { Face } from "geom/brep";
import { faceNew, faceByFourPoints, faceByThreePoints, facesByFourPoints, faceReverse } from "geom/brep";
import { addConvexPlanesToBSPLeaf } from "geom/bsp";
import { clipFace, clippingNodeNew } from "geom/bspClipping";
import type { NodeBSP } from "geom/bspTypes";
import { ANGLE_EPSILON, DIST_EPSILON, DIST_EPSILON_SQUARED, Line2Side } from "geom/core";
import { line2Collinear, line2IsParallel, line2Side } from "geom/line2";
import { offsetPolyLine2, offsetPolyLine3InXYPlane } from "geom/offset";
import { planeCopy, planeRotateAroundNormal, planeNewXY, planeNormal } from "geom/plane";
import { planeBSPFromPlane } from "geom/planeBSP";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2Copy,
  polyLine2EdgeNormal,
  polyLine2EdgeTangent,
  polyLine2Rotate,
  polyLine2EdgeLength,
  polyLine2End,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
  polyLine2Start,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import {
  polyLine3EdgeCount,
  polyLine3EvalEdge,
  polyLine3ExtendEnd,
  polyLine3ExtendStart,
  polyLine3New,
  polyLine3OnPlane,
} from "geom/polyLine3";
import { ray3PerpDistance } from "geom/ray3";
import { adjustExtrusionFaceV } from "geom/threeRailExtrusion";
import { ascendingSortCompare } from "geom/utils";
import { vec3DistSquared, vec3SignedAngleBetween } from "geom/vec3";
import { getHeelHeightOffset } from "../roofs/roofHeelHeightOffsets";
import {
  assertIsEvaluatedRoofSlopeOptions,
  buildSlopes,
  getBaseToPeak,
  getExtensionAtHeightFromBase,
  getFirstSlope,
  getHalfSpan,
  getLastSlope,
  getSlopeAtHeightFromBase,
  getTruncatedHeight,
  isValidExtension,
} from "../roofs/roofSlopes";
import { DEFAULT_ROOF_TRUNCATION_OPTIONS } from "../roofs/roofTruncationDefaults";

interface MakeGableRoofExtrusionOptions {
  readonly peakRail: PolyLine2;
  readonly verticalRails: GableRoofVerticalRails;
  readonly hasStartExtension: boolean;
  readonly hasEndExtension: boolean;
  readonly shedRoofClipPlane?: Plane;
  readonly startOffsetV?: number;
  readonly endOffsetV?: number;
}

export function makeGableRoofExtrusion({
  peakRail,
  verticalRails,
  hasStartExtension,
  hasEndExtension,
  shedRoofClipPlane = undefined,
  startOffsetV = 0,
  endOffsetV = 0,
}: MakeGableRoofExtrusionOptions): GableRoofExtrusion {
  const { startLeftRail, startRightRail, endLeftRail, endRightRail } = verticalRails;

  let clipRoofFaceBSP: NodeBSP | null = null;

  if (shedRoofClipPlane) {
    clipRoofFaceBSP = addConvexPlanesToBSPLeaf([planeBSPFromPlane(shedRoofClipPlane)]);
  }

  const leftFaces: Face[] = [];
  const rightFaces: Face[] = [];
  const startHipFaces: Face[] = [];
  const endHipFaces: Face[] = [];
  const startFaces: Face[] = [];
  const endFaces: Face[] = [];
  const convexVolumes: GableRoofConvexVolume[] = [];

  const railLength = startLeftRail.length;
  if (!(railLength >= 6)) {
    console.assert(false);
  }
  if (!(railLength === startRightRail.length)) {
    console.assert(false);
  }
  if (!(railLength === endLeftRail.length)) {
    console.assert(false);
  }
  if (!(railLength === endRightRail.length)) {
    console.assert(false);
  }

  // helper for building a bottom/top face
  const makeHorizontalFace = (i: number, reverse: boolean = false): Face | null => {
    const i1 = i + 1;
    const i2 = i + 2;

    // face can be null
    const face = faceByFourPoints(
      startLeftRail[i],
      startLeftRail[i1],
      startLeftRail[i2],
      endLeftRail[i],
      endLeftRail[i1],
      endLeftRail[i2],
      endRightRail[i],
      endRightRail[i1],
      endRightRail[i2],
      startRightRail[i],
      startRightRail[i1],
      startRightRail[i2],
    );

    if (reverse) {
      //protects `faceReverse` from null face.
      return face ? faceReverse(face) : null;
    } else {
      return face;
    }
  };

  // Helper function for adjusting the face rotation if its U vector is not parallel to the ground
  // We get as input the desired/unrotated U vector and the face to adjust
  const adjustFaceRotation = (face: Face | null | undefined, refUX: number, refUY: number): Face | null => {
    if (!face) {
      return null;
    }

    const { plane, loops } = face;
    const { uX, uY, uZ } = plane;
    const [nX, nY, nZ] = planeNormal(plane);

    // meassure the signed angle between the face U vector and the reference U vector
    const angle = vec3SignedAngleBetween(nX, nY, nZ, uX, uY, uZ, refUX, refUY, 0);

    // ignore unrotated faces
    const absAngle = Math.abs(angle);
    if (absAngle < ANGLE_EPSILON || Math.abs(absAngle - 2 * Math.PI) < ANGLE_EPSILON) {
      return face;
    }

    // rotate the face plane and the loops in the opposite direction
    const newPlane = planeRotateAroundNormal(planeCopy(plane), angle);
    const newLoops = loops.map((loop) => polyLine2Rotate(polyLine2Copy(loop), -angle));

    return faceNew(newLoops, newPlane);
  };

  // helpers for building a roof side face
  const makeSideFace = (
    pla: PolyLine3,
    plb: PolyLine3,
    i: number,
    j: number,
    refUX: number,
    refUY: number,
  ): Face | null => {
    const i1 = i + 1;
    const i2 = i + 2;

    const j1 = j + 1;
    const j2 = j + 2;

    const x0 = plb[i];
    const y0 = plb[i1];
    const z0 = plb[i2];

    const x1 = pla[i];
    const y1 = pla[i1];
    const z1 = pla[i2];

    const x2 = pla[j];
    const y2 = pla[j1];
    const z2 = pla[j2];

    const x3 = plb[j];
    const y3 = plb[j1];
    const z3 = plb[j2];

    // check for peak vertices
    const face =
      vec3DistSquared(x2, y2, z2, x3, y3, z3) < DIST_EPSILON_SQUARED
        ? faceByThreePoints(x0, y0, z0, x1, y1, z1, x2, y2, z2)
        : faceByFourPoints(x0, y0, z0, x1, y1, z1, x2, y2, z2, x3, y3, z3, false);

    return adjustFaceRotation(face, refUX, refUY);
  };

  const makeSideFaces = (
    pla: PolyLine3,
    plb: PolyLine3,
    i: number,
    j: number,
    refUX: number,
    refUY: number,
  ): Face[] => {
    const i1 = i + 1;
    const i2 = i + 2;

    const j1 = j + 1;
    const j2 = j + 2;

    const x0 = plb[i];
    const y0 = plb[i1];
    const z0 = plb[i2];

    const x1 = pla[i];
    const y1 = pla[i1];
    const z1 = pla[i2];

    const x2 = pla[j];
    const y2 = pla[j1];
    const z2 = pla[j2];

    const x3 = plb[j];
    const y3 = plb[j1];
    const z3 = plb[j2];

    // check for peak vertices
    const faces =
      vec3DistSquared(x2, y2, z2, x3, y3, z3) < DIST_EPSILON_SQUARED
        ? [faceByThreePoints(x0, y0, z0, x1, y1, z1, x2, y2, z2)]
        : facesByFourPoints(x0, y0, z0, x1, y1, z1, x2, y2, z2, x3, y3, z3);

    return faces.map((face) => adjustFaceRotation(face, refUX, refUY)).filter((x) => !!x);
  };

  // helper for adding roof top faces
  let maxV = 0;
  const addRoofTops = (
    pl1: PolyLine3,
    pl2: PolyLine3,
    refUX: number,
    refUY: number,
    faces: Face[],
    capFaces: Face[] | null = null,
    offsetV: number = 0,
  ) => {
    let startV = offsetV;

    // iterate sections
    for (let i = 0, l = railLength - 3; i < l; i += 3) {
      // build face
      const face = makeSideFace(pl1, pl2, i, i + 3, refUX, refUY);

      if (!face) {
        continue;
      }

      // adjust V for contiguous texture coordinates
      adjustExtrusionFaceV(face, startV);

      // classify face
      const [, , nZ] = planeNormal(face.plane);
      const isCapFace = nZ <= DIST_EPSILON;
      if (!isCapFace) {
        clipFace({
          face,
          options: {
            clippingNodes: [clippingNodeNew(clipRoofFaceBSP, true)],
            epsilon: MULTIGABLE_BSP_EPSILON,
          },
          clippedFaces: faces,
        });
      } else if (capFaces) {
        capFaces.push(face);
      }

      // update base V coordinate
      const lowerPl = pl1[i + 2] < pl2[i + 2] ? pl1 : pl2;

      startV += ray3PerpDistance(
        lowerPl[i],
        lowerPl[i + 1],
        lowerPl[i + 2],
        refUX,
        refUY,
        0,
        lowerPl[i + 3],
        lowerPl[i + 4],
        lowerPl[i + 5],
      );
      maxV = Math.max(maxV, startV);

      // reset V
      if (isCapFace) {
        startV = offsetV;
      }
    }
  };

  // build the reference directions
  const [leftRefX, leftRefY] = polyLine2EdgeTangent(peakRail, 0, true);
  const [rightRefX, rightRefY] = polyLine2EdgeTangent(peakRail, 0);
  const [startRefX, startRefY] = polyLine2EdgeNormal(peakRail, 0);
  const [endRefX, endRefY] = polyLine2EdgeNormal(peakRail, 0, true);

  // build basic faces
  const bottomFace = makeHorizontalFace(0);
  const defaultStartFace = hasStartExtension
    ? null
    : makeSideFace(startRightRail, startLeftRail, 0, railLength - 3, endRefX, endRefY);
  const defaultEndFace = hasEndExtension
    ? null
    : makeSideFace(endLeftRail, endRightRail, 0, railLength - 3, endRefX, endRefY);

  // build one convex volume per section
  for (let i = 0, l = railLength - 3; i < l; i += 3) {
    const nextI = i + 3;
    convexVolumes.push({
      leftFace: makeSideFace(startLeftRail, endLeftRail, i, nextI, leftRefX, leftRefY),
      rightFace: makeSideFace(endRightRail, startRightRail, i, nextI, rightRefX, rightRefY),
      startFaces: makeSideFaces(startRightRail, startLeftRail, i, nextI, startRefX, startRefY),
      endFaces: makeSideFaces(endLeftRail, endRightRail, i, nextI, endRefX, endRefY),
      bottomFace: makeHorizontalFace(i),
      topFace: nextI < l ? makeHorizontalFace(nextI, true) : null,
    });
  }

  // build roof tops
  addRoofTops(startLeftRail, endLeftRail, leftRefX, leftRefY, leftFaces);
  addRoofTops(endRightRail, startRightRail, rightRefX, rightRefY, rightFaces);
  addRoofTops(endLeftRail, endRightRail, endRefX, endRefY, endHipFaces, endFaces, endOffsetV);
  addRoofTops(startRightRail, startLeftRail, startRefX, startRefY, startHipFaces, startFaces, startOffsetV);

  return {
    leftFaces,
    rightFaces,
    startHipFaces,
    endHipFaces,
    bottomFace,
    startFaces,
    endFaces,
    startFace: startFaces.length > 0 ? startFaces[0] : defaultStartFace,
    endFace: endFaces.length > 0 ? endFaces[0] : defaultEndFace,
    convexVolumes,
    peakTextureV: maxV,
  };
}

interface GetTruncatedValuesOptions {
  readonly heelHeightOffsets: RoofHeelHeightOffsets;
  readonly baseSlopes: RoofSlope[];
  readonly baseSlopeOptions: EvaluatedRoofSlopeOptions[];
  readonly truncationOptions: RoofTruncationOptions;
  readonly oppNeighborRoof: NeighborRoof;
  readonly roofOverhang: number;
  readonly baseToPeak: number;
  readonly isEndSide: boolean;
  readonly flipTruncationOrder: boolean;
}

interface TruncatedValues {
  readonly isTruncated: boolean;
  readonly height: number;
  readonly height2: number;
  readonly distance: number;
  readonly slopes: RoofSlope[];
}

function getTruncatedValues({
  heelHeightOffsets,
  baseSlopes,
  baseSlopeOptions,
  truncationOptions,
  oppNeighborRoof,
  roofOverhang,
  baseToPeak,
  isEndSide,
  flipTruncationOrder,
}: GetTruncatedValuesOptions): TruncatedValues {
  let isTruncated = truncationOptions.type !== RoofTruncationType.NONE;

  if (!isTruncated) {
    return {
      isTruncated,
      height: baseToPeak,
      height2: baseToPeak,
      slopes: [],
      distance: 0,
    };
  }

  const { slopeOptions: overrideSlopeOptions, slopeType } = truncationOptions;

  assertIsEvaluatedRoofSlopeOptions(overrideSlopeOptions);

  const truncationOffset = getHeelHeightOffset(
    heelHeightOffsets,
    isEndSide !== flipTruncationOrder ? RoofSide.END : RoofSide.START,
  );
  const truncatedHeight = getTruncatedHeight(truncationOptions, baseToPeak, truncationOffset);
  const heightOffset = truncatedHeight;

  isTruncated = baseToPeak - truncatedHeight > DIST_EPSILON;

  if (!isTruncated) {
    return {
      isTruncated,
      height: baseToPeak,
      height2: baseToPeak,
      slopes: [],
      distance: 0,
    };
  }

  const halfSpan = getHalfSpan(baseSlopeOptions, baseToPeak, roofOverhang);

  let truncationSlopes = buildSlopes({
    heelHeightOffsets,
    slopes: baseSlopeOptions,
    startNeighborSlopes: baseSlopes,
    halfSpan,
    roofOverhang,
    heightOffset,
  });

  if (slopeType === TruncationSlopeType.OVERRIDE && overrideSlopeOptions.length > 0) {
    const halfSpan = getHalfSpan(overrideSlopeOptions, baseToPeak, roofOverhang);
    truncationSlopes = buildSlopes({
      heelHeightOffsets,
      slopes: overrideSlopeOptions,
      startNeighborSlopes: baseSlopes,
      halfSpan,
      roofOverhang,
      heightOffset,
    });
  } else if (slopeType !== TruncationSlopeType.SIDE_SLOPES && oppNeighborRoof && oppNeighborRoof.slopes.length > 0) {
    const slope = getLastSlope(oppNeighborRoof.slopes).slope;
    truncationSlopes = buildSlopes({
      heelHeightOffsets,
      slopes: [evaluatedRoofSlopeOptionsNew(slope)],
      startNeighborSlopes: baseSlopes,
      halfSpan: (baseToPeak - heightOffset) / slope,
      roofOverhang,
      heightOffset,
    });
  }

  const { slope: lastSlope, startDist } = getLastSlope(truncationSlopes);
  const baseSlope = getSlopeAtHeightFromBase(truncationSlopes, truncatedHeight);
  const truncatedHeight2 = truncatedHeight + roofOverhang * baseSlope;
  const truncatedDist = startDist + truncatedHeight / lastSlope;

  return {
    isTruncated,
    height: truncatedHeight,
    height2: truncatedHeight2,
    slopes: truncationSlopes,
    distance: truncatedDist,
  };
}

interface BuildWeldingTruncationOptions {
  slopes: RoofSlope[];
  neighborRoof: NeighborRoof;
  baseHeight: number;
}

function buildWeldingTruncation({
  slopes,
  neighborRoof,
  baseHeight,
}: BuildWeldingTruncationOptions): RoofTruncationOptions {
  const { oppSlopes: neighborOppSlopes, baseHeight: neighborBaseHeight } = neighborRoof;

  if (neighborOppSlopes.length <= 0) {
    return DEFAULT_ROOF_TRUNCATION_OPTIONS;
  }

  const relativeHeight = baseHeight - neighborBaseHeight;
  const baseToPeak = getBaseToPeak(slopes);
  const peakElevation = baseToPeak + relativeHeight;
  const neighborPeakElevation = getBaseToPeak(neighborOppSlopes);

  if (peakElevation < neighborPeakElevation + DIST_EPSILON) {
    return DEFAULT_ROOF_TRUNCATION_OPTIONS;
  }

  return {
    type: RoofTruncationType.TRUNCATED,
    heightFactor: neighborPeakElevation - relativeHeight,
    normalizedHeight: false,
    slopeType: TruncationSlopeType.OVERRIDE,
    slopeOptions: [roofSlopeOptionsNew(getLastSlope(neighborOppSlopes).slope)],
  };
}

interface PrepareRoofTruncationOptions {
  readonly heelHeightOffsets: RoofHeelHeightOffsets;
  readonly legacyHeelHeightOffsets: boolean;
  readonly baseSlopes: RoofSlope[];
  readonly baseSlopeOptions: RoofSlopeOptions[];
  readonly truncationOptions: RoofTruncationOptions;
  readonly neighborRoof: NeighborRoof;
  readonly oppNeighborRoof: NeighborRoof;
  readonly peakRail: PolyLine2;
  readonly isFree: boolean;
  readonly isEndSide: boolean;
  readonly flipTruncationOrder: boolean;
  readonly roofOverhang: number;
  readonly baseToPeak: number;
  readonly baseHeight: number;
}

interface RoofTruncationResult {
  readonly isTruncated: boolean;
  readonly isFullyTruncated: boolean;
  readonly truncatedHeight: number;
  readonly truncatedHeight2: number;
  readonly truncatedPeakDist: number;
  readonly truncatedSlopes: RoofSlope[];
}

function prepareRoofTruncation({
  heelHeightOffsets,
  legacyHeelHeightOffsets,
  baseSlopes,
  baseSlopeOptions,
  truncationOptions,
  neighborRoof,
  oppNeighborRoof,
  peakRail,
  isFree,
  isEndSide,
  flipTruncationOrder,
  roofOverhang,
  baseToPeak,
  baseHeight,
}: PrepareRoofTruncationOptions): RoofTruncationResult {
  const weldingTruncationOptions = buildWeldingTruncation({
    slopes: baseSlopes,
    neighborRoof,
    baseHeight,
  });

  assertIsEvaluatedRoofSlopeOptions(baseSlopeOptions);

  const {
    isTruncated: wantsTruncated,
    slopes: truncatedSlopes,
    height: truncatedHeight,
    height2: truncatedHeight2,
  } = getTruncatedValues({
    heelHeightOffsets,
    baseSlopes,
    baseSlopeOptions,
    truncationOptions: isFree ? truncationOptions : weldingTruncationOptions,
    oppNeighborRoof,
    roofOverhang,
    baseToPeak,
    isEndSide,
    flipTruncationOrder,
  });

  const extendFunc = isEndSide ? polyLine2ExtendEnd : polyLine2ExtendStart;
  const truncatedPeakDist =
    getExtensionAtHeightFromBase(truncatedSlopes, baseToPeak) -
    getExtensionAtHeightFromBase(truncatedSlopes, truncatedHeight);

  const availablePeakLength = polyLine2EdgeLength(peakRail, 0);
  const isTruncated =
    wantsTruncated && availablePeakLength > truncatedPeakDist && truncatedHeight <= baseToPeak - DIST_EPSILON;

  const isFullyTruncated = legacyHeelHeightOffsets
    ? isTruncated && truncatedHeight <= DIST_EPSILON
    : isTruncated && truncatedHeight + heelHeightOffsets.refOffset <= DIST_EPSILON;

  if (isTruncated) {
    extendFunc(peakRail, -truncatedPeakDist);
  }

  return { isTruncated, isFullyTruncated, truncatedHeight, truncatedHeight2, truncatedPeakDist, truncatedSlopes };
}

interface MakeGableRoofVerticalRailsOptions {
  readonly heelHeightOffsets: RoofHeelHeightOffsets;
  readonly insertedRoofVolumes: Table<GeneratedRoofVolumeLegacy>;
  readonly leftRail: PolyLine2;
  readonly rightRail: PolyLine2;
  readonly peakRail: PolyLine2;
  readonly baseHeight: number;
  readonly baseToPeak: number;
  readonly leftSideSlopes: RoofSlope[];
  readonly rightSideSlopes: RoofSlope[];
  readonly baseSlopes: RoofSlope[];
  readonly baseSlopeOptions: RoofSlopeOptions[];
  readonly startTruncation: RoofTruncationOptions;
  readonly endTruncation: RoofTruncationOptions;
  readonly startLeftExtensions: number[];
  readonly startRightExtensions: number[];
  readonly endLeftExtensions: number[];
  readonly endRightExtensions: number[];
  readonly neighborRoofs: NeighborRoofs;
  readonly roofType: GableRoofType;
  readonly leftRoofOverhang: number;
  readonly rightRoofOverhang: number;
  readonly startRoofOverhang: number;
  readonly endRoofOverhang: number;
  readonly isStartFree: boolean;
  readonly isEndFree: boolean;
  readonly roofSettings: RoofSettings;
  readonly wallSettings: WallSettings;
  readonly startBaseExtension: number;
  readonly endBaseExtension: number;
  readonly flipTruncationOrder: boolean;
  readonly legacyHeelHeightOffsets: boolean;
}

export function makeGableRoofVerticalRails({
  heelHeightOffsets,
  insertedRoofVolumes,
  leftRail,
  rightRail,
  peakRail,
  baseHeight,
  baseToPeak,
  baseSlopes,
  baseSlopeOptions,
  leftSideSlopes,
  rightSideSlopes,
  startTruncation,
  endTruncation,
  startLeftExtensions,
  startRightExtensions,
  endLeftExtensions,
  endRightExtensions,
  neighborRoofs,
  roofType,
  leftRoofOverhang,
  rightRoofOverhang,
  startRoofOverhang,
  endRoofOverhang,
  isStartFree,
  isEndFree,
  roofSettings,
  wallSettings,
  startBaseExtension,
  endBaseExtension,
  flipTruncationOrder,
  legacyHeelHeightOffsets,
}: MakeGableRoofVerticalRailsOptions): GableRoofVerticalRails {
  const { startNeighbor, endNeighbor, leftNeighbor, rightNeighbor } = neighborRoofs;

  // helper for building and adding the 1 side rails for a roof section
  const addHorizontalRail = (
    rails: PolyLine2[],
    srcRail: PolyLine2,
    railHeight: number,
    railOffset: number,
    startExtension: number,
    endExtension: number,
  ) => {
    const currPlane = planeNewXY(0, 0, baseHeight + railHeight);
    const currRail = polyLine3OnPlane(offsetPolyLine2(srcRail, railOffset), currPlane);

    // extensions first
    polyLine3ExtendStart(currRail, startExtension);
    polyLine3ExtendEnd(currRail, endExtension);

    // contraction after expansions
    if (isStartTruncated) {
      const refStartExtension = getExtensionAtHeightFromBase(truncatedStartSlopes, truncatedStartHeight);
      const truncatedStartExtension =
        railHeight >= truncatedStartHeight
          ? -(getExtensionAtHeightFromBase(truncatedStartSlopes, railHeight) - refStartExtension)
          : 0;

      polyLine3ExtendStart(currRail, truncatedStartExtension);
    }

    if (isEndTruncated) {
      const refEndExtension = getExtensionAtHeightFromBase(truncatedEndSlopes, truncatedEndHeight);
      const truncatedEndExtension =
        railHeight >= truncatedEndHeight
          ? -(getExtensionAtHeightFromBase(truncatedEndSlopes, railHeight) - refEndExtension)
          : 0;

      polyLine3ExtendEnd(currRail, truncatedEndExtension);
    }

    // add rail
    rails.push(currRail);
  };

  // helper for building and adding all rails from one side for a roof section
  const addHorizontalRails = (
    rails: PolyLine2[],
    srcRail: PolyLine2,
    slopes: RoofSlope[],
    oppSlopes: RoofSlope[],
    startExtensions: number[],
    endExtensions: number[],
    offsetSign: number,
  ) => {
    let acumHorizontalDist = 0;
    let extraHeightSplits: number[] = [];

    const firstSlopeHeight = getFirstSlope(slopes).startHeight;
    const firstOppSlopeHeight = getFirstSlope(oppSlopes).startHeight;

    const addTruncationSplit = (truncationOptions: RoofTruncationOptions, isEndSide: boolean) => {
      const truncationOffset = getHeelHeightOffset(
        heelHeightOffsets,
        isEndSide !== flipTruncationOrder ? RoofSide.END : RoofSide.START,
      );
      const truncationHeight = getTruncatedHeight(truncationOptions, baseToPeak, truncationOffset);

      if (truncationHeight < firstSlopeHeight - DIST_EPSILON) {
        extraHeightSplits.push(truncationHeight);
      }
    };

    for (let i = 0, l = oppSlopes.length; i < l; i++) {
      const { startHeight, endHeight } = oppSlopes[i];
      if (startHeight < firstSlopeHeight - DIST_EPSILON) {
        extraHeightSplits.push(endHeight);
      }
    }

    extraHeightSplits.push(firstSlopeHeight);

    // add extra height splits
    if (firstOppSlopeHeight < firstSlopeHeight - DIST_EPSILON) {
      extraHeightSplits.push(firstOppSlopeHeight);
    }

    if (!legacyHeelHeightOffsets) {
      addTruncationSplit(startTruncation, false);
      addTruncationSplit(endTruncation, true);
    }

    // sort
    extraHeightSplits = extraHeightSplits.sort(ascendingSortCompare);

    // filter out duplicates
    extraHeightSplits = extraHeightSplits.filter(
      (x, i, elems) => x > DIST_EPSILON && (i === 0 || Math.abs(x - elems[i - 1]) > DIST_EPSILON),
    );

    for (const extraHeightSplit of extraHeightSplits) {
      addHorizontalRail(rails, srcRail, extraHeightSplit, 0, 0, 0);
    }

    for (let i = 0, l = slopes.length; i < l; i++) {
      const { endHeight, horizontalDist } = slopes[i];

      acumHorizontalDist += horizontalDist;

      // add section rails
      addHorizontalRail(
        rails,
        srcRail,
        endHeight,
        offsetSign * acumHorizontalDist,
        startExtensions[i],
        endExtensions[i],
      );
    }
  };

  // helper function for the truncation slopes for shed roofs
  const getSideNeighborRoof = (isEnd: boolean): NeighborRoof | null => {
    let refNeighborRoof: NeighborRoof | null = null;
    if (roofType === GableRoofType.LEFT_SIDE_SHED_ROOF) {
      refNeighborRoof = rightNeighbor;
    } else if (roofType === GableRoofType.RIGHT_SIDE_SHED_ROOF) {
      refNeighborRoof = leftNeighbor;
    }

    const refRoof = refNeighborRoof?.roofId ? insertedRoofVolumes[refNeighborRoof?.roofId] : undefined;

    if (!refRoof) {
      return null;
    }

    const {
      rails: refRails,
      leftSideSlopes: refLeftSideSlopes,
      rightSideSlopes: refRightSideSlopes,
      heelHeightOffsets: refHeelHeightOffsets,
      baseHeight: refBaseHeight,
      rectangularOverhangs: refRoofRectangularOverhangs,
    } = refRoof;

    const {
      leftOverhang: refLeftRoofOverhang,
      rightOverhang: refRightRoofOverhang,
      startOverhang: refRoofStartOverhang,
      endOverhang: refRoofEndOverhang,
    } = refRoofRectangularOverhangs;

    const { peakRail: refPeakRail } = refRails;
    const [x0, y0] = isEnd ? polyLine2End(peakRail) : polyLine2Start(peakRail);
    const side = line2Side(refPeakRail[0], refPeakRail[1], refPeakRail[3], refPeakRail[4], x0, y0);

    const sideSlopes = side === Line2Side.LEFT ? refLeftSideSlopes : refRightSideSlopes;
    const oppSideSlope = side === Line2Side.LEFT ? refRightSideSlopes : refLeftSideSlopes;
    const sideRoofOverhang = side === Line2Side.LEFT ? refRightRoofOverhang : refLeftRoofOverhang;
    const baseHeightOffset =
      (side === Line2Side.LEFT ? refHeelHeightOffsets.leftOffset : refHeelHeightOffsets.rightOffset) +
      refHeelHeightOffsets.refOffset -
      refBaseHeight;

    assertIsNotNull(refNeighborRoof);

    return neighborRoofNew(
      refBaseHeight,
      baseHeightOffset,
      sideRoofOverhang,
      refRoofStartOverhang,
      refRoofEndOverhang,
      refNeighborRoof.relativeDist,
      refNeighborRoof.depth,
      sideSlopes,
      oppSideSlope,
      refNeighborRoof.roofId,
      refNeighborRoof.side,
    );
  };

  const startSideNeighbor: NeighborRoof | null = getSideNeighborRoof(false);
  const endSideNeighbor: NeighborRoof | null = getSideNeighborRoof(true);

  // prepare roof end side truncations
  const startTruncationOptions: PrepareRoofTruncationOptions = {
    heelHeightOffsets,
    legacyHeelHeightOffsets,
    baseSlopes,
    baseSlopeOptions,
    truncationOptions: startTruncation,
    neighborRoof: startNeighbor,
    oppNeighborRoof: startSideNeighbor ?? endNeighbor,
    peakRail,
    isFree: isStartFree,
    isEndSide: false,
    roofOverhang: startRoofOverhang,
    baseToPeak,
    baseHeight,
    flipTruncationOrder,
  };

  const endTruncationOptions: PrepareRoofTruncationOptions = {
    heelHeightOffsets,
    legacyHeelHeightOffsets,
    baseSlopes,
    baseSlopeOptions,
    truncationOptions: endTruncation,
    neighborRoof: endNeighbor,
    oppNeighborRoof: endSideNeighbor ?? startNeighbor,
    peakRail,
    isFree: isEndFree,
    isEndSide: true,
    roofOverhang: endRoofOverhang,
    baseToPeak,
    baseHeight,
    flipTruncationOrder,
  };

  let startTruncationResult: RoofTruncationResult;
  let endTruncationResult: RoofTruncationResult;

  if (flipTruncationOrder) {
    endTruncationResult = prepareRoofTruncation(endTruncationOptions);
    startTruncationResult = prepareRoofTruncation(startTruncationOptions);
  } else {
    startTruncationResult = prepareRoofTruncation(startTruncationOptions);
    endTruncationResult = prepareRoofTruncation(endTruncationOptions);
  }

  const {
    isTruncated: isStartTruncated,
    isFullyTruncated: isStartFullyTruncated,
    truncatedHeight: truncatedStartHeight,
    truncatedHeight2: truncatedStartHeight2,
    truncatedPeakDist: truncatedStartPeakDist,
    truncatedSlopes: truncatedStartSlopes,
  } = startTruncationResult;

  const {
    isTruncated: isEndTruncated,
    isFullyTruncated: isEndFullyTruncated,
    truncatedHeight: truncatedEndHeight,
    truncatedHeight2: truncatedEndHeight2,
    truncatedPeakDist: truncatedEndPeakDist,
    truncatedSlopes: truncatedEndSlopes,
  } = endTruncationResult;

  const { eaveExtensionAtNonCoplanarRake = 0, overhangDatum = WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING } =
    roofSettings;
  const wallOffsetWithSheathing = getExteriorWallOffset(wallSettings, overhangDatum);

  const leftRails: PolyLine3[] = [];
  const rightRails: PolyLine3[] = [];

  const hasStartExtension = isValidExtension(startLeftExtensions) || isValidExtension(startRightExtensions);
  const hasEndExtension = isValidExtension(endLeftExtensions) || isValidExtension(endRightExtensions);

  let startLeftBaseExtension = 0.0;
  let startRightBaseExtension = 0.0;
  let endLeftBaseExtension = 0.0;
  let endRightBaseExension = 0.0;

  const startNeighborRoof = insertedRoofVolumes[startNeighbor.roofId];
  const endNeighborRoof = insertedRoofVolumes[endNeighbor.roofId];

  if (hasStartExtension && !startNeighborRoof.isEndFullyTruncated) {
    const { peakRail: neighborPeak, startRail: neighborStartRail, endRail: neighborEndRail } = startNeighborRoof.rails;

    const areParallel = line2IsParallel(
      peakRail[0],
      peakRail[1],
      peakRail[2],
      peakRail[3],
      neighborPeak[0],
      neighborPeak[1],
      neighborPeak[3],
      neighborPeak[4],
    );

    const offsetLeftRail = offsetPolyLine2(leftRail, leftRoofOverhang);
    const offsetRightRail = offsetPolyLine2(rightRail, -rightRoofOverhang);

    const offsetNeighborStartRail = offsetPolyLine3InXYPlane(
      neighborStartRail,
      -(startNeighbor.startOverhang - wallOffsetWithSheathing),
    );
    const offsetNeighborEndRail = offsetPolyLine3InXYPlane(
      neighborEndRail,
      startNeighbor.endOverhang - wallOffsetWithSheathing,
    );

    const areLeftAndStartAligned = line2Collinear(
      offsetLeftRail[0],
      offsetLeftRail[1],
      offsetLeftRail[2],
      offsetLeftRail[3],
      offsetNeighborStartRail[0],
      offsetNeighborStartRail[1],
      offsetNeighborStartRail[3],
      offsetNeighborStartRail[4],
    );
    const areLeftAndEndAligned = line2Collinear(
      offsetLeftRail[0],
      offsetLeftRail[1],
      offsetLeftRail[2],
      offsetLeftRail[3],
      offsetNeighborEndRail[0],
      offsetNeighborEndRail[1],
      offsetNeighborEndRail[3],
      offsetNeighborEndRail[4],
    );

    const areRightAndStartAligned = line2Collinear(
      offsetRightRail[0],
      offsetRightRail[1],
      offsetRightRail[2],
      offsetRightRail[3],
      offsetNeighborStartRail[0],
      offsetNeighborStartRail[1],
      offsetNeighborStartRail[3],
      offsetNeighborStartRail[4],
    );
    const areRightAndEndAligned = line2Collinear(
      offsetRightRail[0],
      offsetRightRail[1],
      offsetRightRail[2],
      offsetRightRail[3],
      offsetNeighborEndRail[0],
      offsetNeighborEndRail[1],
      offsetNeighborEndRail[3],
      offsetNeighborEndRail[4],
    );

    const areLeftAligned = areLeftAndStartAligned || areLeftAndEndAligned;
    const areRightAligned = areRightAndStartAligned || areRightAndEndAligned;

    let leftOverhangDiff = 0;
    let rightOverhangDiff = 0;

    if (areLeftAndStartAligned) {
      leftOverhangDiff = leftRoofOverhang - (startNeighbor.startOverhang - wallOffsetWithSheathing);
    } else if (areLeftAndEndAligned) {
      leftOverhangDiff = leftRoofOverhang - (startNeighbor.endOverhang - wallOffsetWithSheathing);
    }

    if (areRightAndStartAligned) {
      rightOverhangDiff = rightRoofOverhang - (startNeighbor.startOverhang - wallOffsetWithSheathing);
    } else if (areRightAndEndAligned) {
      rightOverhangDiff = rightRoofOverhang - (startNeighbor.endOverhang - wallOffsetWithSheathing);
    }

    const leftHeighOffset = leftOverhangDiff * getFirstSlope(leftSideSlopes).slope;
    const rightHeighOffset = rightOverhangDiff * getFirstSlope(rightSideSlopes).slope;

    const alpha = Math.abs(Math.atan(getFirstSlope(startNeighbor.slopes).slope));
    const beta = Math.PI / 2 - alpha;
    const {
      rakeFasciaSize,
      eaveType,
      eaveReturnDistance = DEFAULT_EAVE_RETURN_DISTANCE,
    } = startNeighborRoof.roofSettings;

    let leftFasciaOffset = (rakeFasciaSize + GABLE_ROOF_THICKNESS) / Math.cos(beta);
    let rightFasciaOffset = leftFasciaOffset;

    if (isBoxEave[eaveType]) {
      const boxWidth = eaveReturnDistance + startNeighbor.overhang - wallOffsetWithSheathing;
      const boxHeight = boxWidth * getFirstSlope(startNeighbor.slopes).slope;

      if (leftHeighOffset < boxHeight && leftHeighOffset >= -DIST_EPSILON && boxHeight > DIST_EPSILON) {
        leftFasciaOffset = Math.max(leftFasciaOffset, boxWidth * (1 - leftHeighOffset / boxHeight));
      }

      if (rightHeighOffset < boxHeight && rightHeighOffset >= -DIST_EPSILON && boxHeight > DIST_EPSILON) {
        rightFasciaOffset = Math.max(rightFasciaOffset, boxWidth * (1 - rightHeighOffset / boxHeight));
      }
    }

    if ((!areParallel && leftOverhangDiff > DIST_EPSILON && areLeftAligned) || areParallel) {
      startLeftBaseExtension =
        getExtensionAtHeightFromBase(startNeighbor.slopes, leftHeighOffset) +
        leftFasciaOffset +
        eaveExtensionAtNonCoplanarRake;

      startLeftExtensions[0] = startLeftBaseExtension;
      for (let i = 1, l = startLeftExtensions.length; i < l && leftSideSlopes[i].endDist <= leftRoofOverhang; i++) {
        startLeftExtensions[i] = startLeftBaseExtension;
      }
    }

    if ((!areParallel && rightOverhangDiff > DIST_EPSILON && areRightAligned) || areParallel) {
      startRightBaseExtension =
        getExtensionAtHeightFromBase(startNeighbor.slopes, rightHeighOffset) +
        rightFasciaOffset +
        eaveExtensionAtNonCoplanarRake;

      startRightExtensions[0] = startRightBaseExtension;
      for (let i = 1, l = startRightExtensions.length; i < l && rightSideSlopes[i].endDist <= rightRoofOverhang; i++) {
        startRightExtensions[i] = startRightBaseExtension;
      }
    }

    startBaseExtension += Math.max(startLeftBaseExtension, startRightBaseExtension);
  } else if (hasStartExtension && startNeighbor.isTruncated) {
    startLeftBaseExtension += startNeighbor.overhang;
    startRightBaseExtension += startNeighbor.overhang;

    startLeftExtensions[0] += startNeighbor.overhang;
    startRightExtensions[0] += startNeighbor.overhang;
  }

  if (hasEndExtension && !endNeighborRoof.isStartFullyTruncated) {
    const { peakRail: neighborPeak, startRail: neighborStartRail, endRail: neighborEndRail } = endNeighborRoof.rails;

    const areParallel = line2IsParallel(
      peakRail[0],
      peakRail[1],
      peakRail[2],
      peakRail[3],
      neighborPeak[0],
      neighborPeak[1],
      neighborPeak[3],
      neighborPeak[4],
    );

    const offsetLeftRail = offsetPolyLine2(leftRail, leftRoofOverhang);
    const offsetRightRail = offsetPolyLine2(rightRail, -rightRoofOverhang);

    const offsetNeighborStartRail = offsetPolyLine3InXYPlane(
      neighborStartRail,
      -(endNeighbor.startOverhang - wallOffsetWithSheathing),
    );
    const offsetNeighborEndRail = offsetPolyLine3InXYPlane(
      neighborEndRail,
      endNeighbor.endOverhang - wallOffsetWithSheathing,
    );

    const areLeftAndStartAligned = line2Collinear(
      offsetLeftRail[0],
      offsetLeftRail[1],
      offsetLeftRail[2],
      offsetLeftRail[3],
      offsetNeighborStartRail[0],
      offsetNeighborStartRail[1],
      offsetNeighborStartRail[3],
      offsetNeighborStartRail[4],
    );
    const areLeftAndEndAligned = line2Collinear(
      offsetLeftRail[0],
      offsetLeftRail[1],
      offsetLeftRail[2],
      offsetLeftRail[3],
      offsetNeighborEndRail[0],
      offsetNeighborEndRail[1],
      offsetNeighborEndRail[3],
      offsetNeighborEndRail[4],
    );

    const areRightAndStartAligned = line2Collinear(
      offsetRightRail[0],
      offsetRightRail[1],
      offsetRightRail[2],
      offsetRightRail[3],
      offsetNeighborStartRail[0],
      offsetNeighborStartRail[1],
      offsetNeighborStartRail[3],
      offsetNeighborStartRail[4],
    );
    const areRightAndEndAligned = line2Collinear(
      offsetRightRail[0],
      offsetRightRail[1],
      offsetRightRail[2],
      offsetRightRail[3],
      offsetNeighborEndRail[0],
      offsetNeighborEndRail[1],
      offsetNeighborEndRail[3],
      offsetNeighborEndRail[4],
    );

    const areLeftAligned = areLeftAndStartAligned || areLeftAndEndAligned;
    const areRightAligned = areRightAndStartAligned || areRightAndEndAligned;

    let leftOverhangDiff = 0;
    let rightOverhangDiff = 0;

    if (areLeftAndStartAligned) {
      leftOverhangDiff = leftRoofOverhang - (endNeighbor.startOverhang - wallOffsetWithSheathing);
    } else if (areLeftAndEndAligned) {
      leftOverhangDiff = leftRoofOverhang - (endNeighbor.endOverhang - wallOffsetWithSheathing);
    }

    if (areRightAndStartAligned) {
      rightOverhangDiff = rightRoofOverhang - (endNeighbor.startOverhang - wallOffsetWithSheathing);
    } else if (areRightAndEndAligned) {
      rightOverhangDiff = rightRoofOverhang - (endNeighbor.endOverhang - wallOffsetWithSheathing);
    }

    const leftHeighOffset = leftOverhangDiff * getFirstSlope(leftSideSlopes).slope;
    const rightHeighOffset = rightOverhangDiff * getFirstSlope(rightSideSlopes).slope;

    const alpha = Math.abs(Math.atan(getFirstSlope(endNeighbor.slopes).slope));
    const beta = Math.PI / 2 - alpha;
    const {
      rakeFasciaSize,
      eaveType,
      eaveReturnDistance = DEFAULT_EAVE_RETURN_DISTANCE,
    } = endNeighborRoof.roofSettings;

    let leftFasciaOffset = (rakeFasciaSize + GABLE_ROOF_THICKNESS) / Math.cos(beta);
    let rightFasciaOffset = leftFasciaOffset;

    if (isBoxEave[eaveType]) {
      const boxWidth = eaveReturnDistance + endNeighbor.overhang - wallOffsetWithSheathing;
      const boxHeight = boxWidth * getFirstSlope(endNeighbor.slopes).slope;

      if (leftHeighOffset < boxHeight && leftHeighOffset >= -DIST_EPSILON && boxHeight > DIST_EPSILON) {
        leftFasciaOffset = Math.max(leftFasciaOffset, boxWidth * (1 - leftHeighOffset / boxHeight));
      }

      if (rightHeighOffset < boxHeight && rightHeighOffset >= -DIST_EPSILON && boxHeight > DIST_EPSILON) {
        rightFasciaOffset = Math.max(rightFasciaOffset, boxWidth * (1 - rightHeighOffset / boxHeight));
      }
    }

    if ((!areParallel && leftOverhangDiff > DIST_EPSILON && areLeftAligned) || areParallel) {
      endLeftBaseExtension =
        getExtensionAtHeightFromBase(endNeighbor.slopes, leftHeighOffset) +
        leftFasciaOffset +
        eaveExtensionAtNonCoplanarRake;

      endLeftExtensions[0] = endLeftBaseExtension;
      for (let i = 1, l = endLeftExtensions.length; i < l && leftSideSlopes[i].endDist <= leftRoofOverhang; i++) {
        endLeftExtensions[i] = endLeftBaseExtension;
      }
    }

    if ((!areParallel && rightOverhangDiff > DIST_EPSILON && areRightAligned) || areParallel) {
      endRightBaseExension =
        getExtensionAtHeightFromBase(endNeighbor.slopes, rightHeighOffset) +
        rightFasciaOffset +
        eaveExtensionAtNonCoplanarRake;

      endRightExtensions[0] = endRightBaseExension;
      for (let i = 1, l = endRightExtensions.length; i < l && rightSideSlopes[i].endDist <= rightRoofOverhang; i++) {
        endRightExtensions[i] = endRightBaseExension;
      }
    }

    endBaseExtension += Math.max(endLeftBaseExtension, endRightBaseExension);
  } else if (hasEndExtension && endNeighbor.isTruncated) {
    endLeftBaseExtension += endNeighbor.overhang;
    endRightBaseExension += endNeighbor.overhang;

    endLeftExtensions[0] += endNeighbor.overhang;
    endRightExtensions[0] += endNeighbor.overhang;
  }

  // add the base rails
  addHorizontalRail(leftRails, leftRail, 0, 0, startLeftBaseExtension, endLeftBaseExtension);
  addHorizontalRail(rightRails, rightRail, 0, 0, startRightBaseExtension, endRightBaseExension);

  // iterate sections
  addHorizontalRails(leftRails, leftRail, leftSideSlopes, rightSideSlopes, startLeftExtensions, endLeftExtensions, 1);
  addHorizontalRails(
    rightRails,
    rightRail,
    rightSideSlopes,
    leftSideSlopes,
    startRightExtensions,
    endRightExtensions,
    -1,
  );

  // convert to 4 vertical rails
  const leftVerticalRailElemCount = leftRails.length * 3;
  const startLeftRail = polyLine3New(leftVerticalRailElemCount);
  const endLeftRail = polyLine3New(leftVerticalRailElemCount);

  for (let i = 0, v = 0, l = leftRails.length; i < l; i++, v += 3) {
    if (!leftRails[i]) {
      continue;
    }
    const edgeCount = polyLine3EdgeCount(leftRails[i]);
    const [x0, y0, z0] = polyLine3EvalEdge(leftRails[i], 0, 0);
    const [x1, y1, z1] = polyLine3EvalEdge(leftRails[i], edgeCount - 1, 1, true);

    startLeftRail[v] = x0;
    startLeftRail[v + 1] = y0;
    startLeftRail[v + 2] = z0;

    endLeftRail[v] = x1;
    endLeftRail[v + 1] = y1;
    endLeftRail[v + 2] = z1;
  }

  const rightVerticalRailElemCount = rightRails.length * 3;
  const endRightRail = polyLine3New(rightVerticalRailElemCount);
  const startRightRail = polyLine3New(rightVerticalRailElemCount);

  for (let i = 0, v = 0, l = rightRails.length; i < l; i++, v += 3) {
    if (!rightRails[i]) {
      continue;
    }
    const edgeCount = polyLine3EdgeCount(rightRails[i]);
    if (edgeCount < 1) {
      continue;
    }

    const [x0, y0, z0] = polyLine3EvalEdge(rightRails[i], 0, 0);
    const [x1, y1, z1] = polyLine3EvalEdge(rightRails[i], edgeCount - 1, 1, true);

    startRightRail[v] = x0;
    startRightRail[v + 1] = y0;
    startRightRail[v + 2] = z0;

    endRightRail[v] = x1;
    endRightRail[v + 1] = y1;
    endRightRail[v + 2] = z1;
  }

  return {
    isStartTruncated,
    isStartFullyTruncated,
    truncatedStartHeight,
    truncatedStartHeight2,
    truncatedStartPeakDist,
    isEndTruncated,
    isEndFullyTruncated,
    truncatedEndHeight,
    truncatedEndHeight2,
    truncatedEndPeakDist,
    startLeftRail,
    startRightRail,
    endLeftRail,
    endRightRail,
    truncatedStartSlopes,
    truncatedEndSlopes,
    startBaseExtension,
    endBaseExtension,
  };
}
